import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
// Shared compact landing for multilingual service/apostille/translate hubs.
// Pure presentational; all copy comes from the `content` prop so each route
// supplies native-language text. Used by /zh/apostille, /zh/translate,
// /zh/services and future Phase M1+ pages.
import { Link } from "@tanstack/react-router";
import LocaleServiceLinks from "@/components/LocaleServiceLinks";

export interface MLServiceContent {
  langCode: string;
  dir?: "ltr" | "rtl";
  eyebrow: string;
  h1: string;
  intro: string;
  ctaPrimary: string;
  ctaPrimaryHref: string;
  ctaSecondary: string;
  ctaSecondaryTo: string;
  contactHint: string;
  factsLabel: string;
  facts: { label: string; value: string }[];
  highlightsH2: string;
  highlights: { title: string; body: string }[];
  stepsH2: string;
  steps: { name: string; text: string }[];
  faqH2: string;
  faqs: { q: string; a: string }[];
  citationsH2: string;
  citations: { label: string; href: string }[];
  bottomNudge: string;
}

export function MultilingualServiceLanding({ c }: { c: MLServiceContent }) {
  return (
    <main className="min-h-screen bg-white" lang={c.langCode} dir={c.dir ?? "ltr"}>
      <section className="bg-navy-deep px-4 py-14 text-white">
        <div className="mx-auto max-w-5xl">
          <p className="text-xs uppercase tracking-widest text-amber-300">{c.eyebrow}</p>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">{c.h1}</h1>
          <p data-speakable className="mt-4 max-w-2xl text-white/85">{c.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href={c.ctaPrimaryHref} className="rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-navy-deep hover:bg-amber-300">{c.ctaPrimary}</a>
            <Link to={c.ctaSecondaryTo} className="rounded-full border border-white/30 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10">{c.ctaSecondary}</Link>
          </div>
          <p className="mt-3 text-xs text-white/60">{c.contactHint}</p>

          {/* AEO Quick Answer — speakable Q/A microdata (schema.org/Question) */}
          <div
            className="mt-8 rounded-2xl border border-amber-300/40 bg-white/[0.04] p-5 sm:p-6"
            itemScope
            itemType="https://schema.org/Question"
            data-speakable="true"
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300">AI Quick Answer</p>
            <h2 className="mt-1 text-base font-bold text-white sm:text-lg" itemProp="name">{c.h1}</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="mt-2 text-sm leading-relaxed text-white/85 sm:text-base" itemProp="text">{c.intro}</p>
            </div>
          </div>

          {/* Atomic / quotable stats — language-neutral numerics for AI Overview */}
          {c.facts.length > 0 && (
            <ul className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3" data-speakable="true">
              {c.facts.slice(0, 4).map((f) => (
                <li key={f.label} className="rounded-xl border border-white/10 bg-white/5 p-3 text-center">
                  <div className="text-sm font-extrabold text-amber-300 sm:text-base">{f.value}</div>
                  <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-white/80">{f.label}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <h2 className="sr-only">{c.factsLabel}</h2>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {c.facts.map((f) => (
            <div key={f.label} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <dt className="text-xs uppercase tracking-wide text-slate-500">{f.label}</dt>
              <dd className="mt-1 text-sm font-bold text-navy-deep">{f.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{c.highlightsH2}</h2>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {c.highlights.map((h) => (
            <article key={h.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="text-base font-bold text-navy-deep">{h.title}</h3>
              <p className="mt-2 text-sm text-slate-600">{h.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{c.stepsH2}</h2>
        <ol className="mt-5 space-y-4">
          {c.steps.map((s, i) => (
            <li key={s.name} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-400 text-sm font-bold text-navy-deep">{i + 1}</span>
                <div>
                  <h3 className="text-base font-bold text-navy-deep">{s.name}</h3>
                  <p className="mt-1 text-sm text-slate-600">{s.text}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">{c.faqH2}</h2>
        <div className="mt-5 space-y-3">
          {c.faqs.map((f) => (
            <details key={f.q} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <summary className="cursor-pointer text-base font-bold text-navy-deep">{f.q}</summary>
              <p data-speakable className="voice-faq-answer mt-2 text-sm text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <h2 className="font-display text-xl font-extrabold text-navy-deep">{c.citationsH2}</h2>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-slate-700">
          {c.citations.map((cite) => (
            <li key={cite.href}>
              <a href={cite.href} target="_blank" rel="noopener noreferrer" className="underline hover:text-navy-deep">{cite.label}</a>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-amber-50 px-4 py-10">
        <div className="mx-auto max-w-5xl text-center">
          <p className="text-base font-semibold text-navy-deep">{c.bottomNudge}</p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <a href={c.ctaPrimaryHref} className="rounded-full bg-navy-deep px-5 py-2.5 text-sm font-bold text-white hover:bg-navy-deep/90">{c.ctaPrimary}</a>
            <a href="tel:+66832494999" className="rounded-full border border-navy-deep px-5 py-2.5 text-sm font-semibold text-navy-deep hover:bg-navy-deep/5">+66 83-249-4999</a>
          </div>
        </div>
      </section>
          <ServicePageEnhancer serviceName="NYC Legal" locale={c.langCode as never} />
      <RelatedHubGrid locale={c.langCode === "th" ? "th" : "en"} />
      <LocaleServiceLinks />
</main>
  );
}
